import { from, Observable, Subject as RxjsSubject, zip } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { platform } from '../../platform/platform';
import { ApiError } from '../_errors/api-error';
import { ApplicationError } from '../_errors/application-error';
import { Inspection } from '../_objects/inspection';
import { InspectionImageListItem } from '../_objects/inspection-image';
import { InspectionReport, ReportImageQuality } from '../_objects/inspection-report';
import { SubjectModel } from '../_objects/subject-model';
import { EditableTextList } from '../_offline/models/report-preview/report-template';
import { WkHtmlToPdfOptions } from '../_offline/models/report-preview/wkHtml-to-pdf-options';
import { OfflineUtilsService } from '../_offline/services/offline-utils.service';
import { UtilsService } from '../_services/_utils.service';
import { CommonSnackBarService } from '../_services/common-snack-bar.service';
import { FileSystemAccessService } from '../_services/file-system-access.service';
import { InspectionImageService } from '../_services/http-api/inspection-image.service';
import { InspectionReportService } from '../_services/http-api/inspection-report.service';
import { LoginUserOnlineService } from '../_services/http-api/login-user-online.service';
import { ReportTemplateService } from '../_services/http-api/report-template.service';
import { SubjectModelService } from '../_services/http-api/subject-model.service';
import { TenantConfigService } from '../_services/http-api/tenant-config.service';
import { LangUpdateService } from '../_services/lang-update.service';
import { NetworkStatusService } from '../_services/network-status.service';
import { OfflineReportPreviewService } from '../_services/offline-report-preview.service';
import { SpinnerService } from '../_services/spinner.service';
import { SelectReportService } from '../_services/select-report.service';
import { INSPECTION_REPORT___DISPLAY_PARAMS, StorageService } from '../_services/storage.service';
import {
  BROWSER_TYPE,
  DOCUMENT_FILETYPE,
  GEN_DRAFT_REPORT_TYPE,
  GEN_FINAL_REPORT_TYPE,
  INSPECTION_REPORT_STATUS_CODE,
  PaperKind,
  PDF_COMPRESS_PARAMS_HIGH,
  PDF_COMPRESS_PARAMS_LOW,
  PDF_COMPRESS_PARAMS_MEDIUM,
  PERMISSION_CODE,
  PLATFORM,
  TenantConfig
} from '../app.constants';
import { CommonErrorMessageDialogComponent } from '../common-dialog/common-error-message-dialog.component';
import { CommonShareDialogComponent } from '../common-dialog/common-share-dialog/common-share-dialog.component';
import { ImageEditDialogComponent } from '../inspection-data-management/image-edit-dialog/image-edit-dialog.component';
import { ReportCompleteConfirmDialogComponent } from '../inspection-report/inspection-report-complete-confirm-dialog.component';
import { ReportPropertiesDialogComponent } from '../inspection-report/inspection-report-edit-properties-dialog.component';
import { ReportEditableTextsEditDialogComponent } from '../inspection-report/inspection-report-edit-text-dialog.component';
import { EditableTextsValidationDialogComponent } from '../inspection-report/inspection-report-edit-text-validation-dialog.component';
import { InspectionDataManageService } from '../inspection/inspection-data-manage.service';
import { ReEditConfirmDialogComponent } from './re-edit-confirm-dialog/re-edit-confirm-dialog.component';
import { IndividualReportDbService } from '../_services/database/individual-report-db.service';
import { MeasurementModeName } from '../_objects/measurement-mode-name';
import { getFormatedExpirationDate } from '../_utils/date.utils';
import { BlobService } from '../_services/blob.service';
import { ProgressSnackBarService } from '../_services/progress-snack-bar.service';
import { FILE_SYSTEM } from "../_offline/electron.constants";
import FILE_PREFIX = FILE_SYSTEM.PATH.FILE_PREFIX;
import { EvcModalService } from '../_shared/services/modal/evc-modal.service';

/**
 * 日付表示をyyyy/MM/ddにするために、
 * NativeDateAdapterのformat()をオーバーライドしている。
 */
export class AppDateAdapter extends NativeDateAdapter {

  format(date: Date, displayFormat: string): string {
    if (displayFormat === 'dateInput') {
      // 'dateInput' は yyyy/MM/dd へフォーマットする。
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      return year + '/' + this._to2digit(month) + '/' + this._to2digit(day);
    } else {
      // 'dateInput' 以外はNativeDateAdapterの振る舞いとする。
      return super.format(date, displayFormat);
    }
  }

  private _to2digit(n: number) {
    return ('00' + n).slice(-2);
  }
}
@Component({
    selector: 'app-inspection-report',
    templateUrl: './inspection-report.component.html',
    styleUrls: ['./inspection-report.component.scss'],
    standalone: false
})
export class InspectionReportComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() inspection: Inspection;
  @Input() inspectionReport: InspectionReport;
  @Input() pdfUrl: SafeResourceUrl;
  @Output() changedReport = new EventEmitter<string>();
  @Output() previewGenerated = new EventEmitter<string>();
  @ViewChild('ieIframe') ieIframe: ElementRef;
  @ViewChild('edgeIframe') edgeIframe: ElementRef;
  @ViewChild('iframe') iframe: ElementRef;
  reportId: string;

  isDefaultOfflineUser = false;
  // sanitize
  previewUrl: SafeResourceUrl;

  browserName: string;
  deviceName: string;
  inspectionImage: InspectionImageListItem[];
  subjectModel: SubjectModel;
  editableTextsList: EditableTextList[];

  isEditabletexts = false;
  isPlugin = true;
  isDownload = false;
  // 完了処理中に他のボタンが押せないように
  isProcess = false;
  isDone = false;
  isReload = false;
  isError = false;
  isProcessingCreateReport = false;
  isAdmin = false;
  isReEdit = false;
  latestVersion: number;
  isShowPastReports = true;
  onDestroy$ = new RxjsSubject();
  shouldSkipPreview: boolean = false;

  // レポート内での計測モードの名前を使用言語で保存
  private measurementModeName: MeasurementModeName;

  readonly getReportTime = 10000;
  public isOfflinePreviewReady = false;
  public isSaveThumbnail = false;
  private getReportTimer: NodeJS.Timeout;

  constructor(
    private snackBar: CommonSnackBarService,
    private inspectionReportService: InspectionReportService,
    private reportTemplateService: ReportTemplateService,
    private utilsService: UtilsService,
    private blobService: BlobService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private loginUserService: LoginUserOnlineService,
    private apiError: ApiError,
    private storageService: StorageService,
    private oscSpinnerService: SpinnerService,
    private inspDataManageService: InspectionDataManageService,
    private inspectionImageService: InspectionImageService,
    private subjectModelService: SubjectModelService,
    private networkStatusService: NetworkStatusService,
    private offlineReportPreviewService: OfflineReportPreviewService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private selectReportService: SelectReportService,
    private cd: ChangeDetectorRef,
    private fileSystemAccessService: FileSystemAccessService,
    private offlineUtilsService: OfflineUtilsService,
    private tenantConfigService: TenantConfigService,
    private individualReportDbService: IndividualReportDbService,
    private progressSnackBar: ProgressSnackBarService,
    private evcModalService: EvcModalService,
  ) {
    this.isProcessingCreateReport = false;
    this.isAdmin = this.loginUserService.isAdminRole();

    const displayParam = this.storageService.fetchReportDisplayParam(INSPECTION_REPORT___DISPLAY_PARAMS);
    this.isShowPastReports = this.utilsService.isNullOrUndefined(displayParam?.isShowPastReports)
      || displayParam.isShowPastReports;
    this.langService.prepare(translate, this.onDestroy$);
    this.measurementModeName = new MeasurementModeName(this.translate);
    this.networkStatusService.isOnPremise$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isOnPremise: boolean) => {
        this.isDefaultOfflineUser = isOnPremise;
      });

    this.inspDataManageService.draftPdfIsAvailableObs.pipe(takeUntil(this.onDestroy$)).subscribe(value => {
      if (value === true) {
        this.downloadDraftInspectionReportPdf();
      }
    });

  }

  get isEditing(): boolean {
    return !this.utilsService.isNullOrUndefined(this.inspectionReport)
      && this.inspectionReport.status === INSPECTION_REPORT_STATUS_CODE.EDITING;
  }

  get isCompleted(): boolean {
    return !this.utilsService.isNullOrUndefined(this.inspectionReport)
      && this.inspectionReport.status === INSPECTION_REPORT_STATUS_CODE.COMPLETED;
  }

  get isDraftProcessing(): boolean {
    return !this.utilsService.isNullOrUndefined(this.inspectionReport)
      && this.inspectionReport.status === INSPECTION_REPORT_STATUS_CODE.DRAFT_PROCESSING;
  }

  get isFinalProcessing(): boolean {
    return !this.utilsService.isNullOrUndefined(this.inspectionReport)
      && this.inspectionReport.status === INSPECTION_REPORT_STATUS_CODE.FINAL_PROCESSING;
  }

  get isDesktopAndOffline(): boolean {
    return this.networkStatusService.isDesktopAndOffline;
  }

  get isDeletedInspection(): boolean {
    return !this.inspection || this.inspection.isDeleted;
  }

  get isEditable(): boolean {
    return this.inspection

      && this.inspection.permission && (this.inspection.permission & PERMISSION_CODE.WRITE) !== 0
      && (!this.inspectionReport || this.inspectionReport.status === INSPECTION_REPORT_STATUS_CODE.EDITING
        || this.inspectionReport.status === INSPECTION_REPORT_STATUS_CODE.FAILED);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.inspectionReport && this.inspectionReport) {
      this.reportId = this.inspectionReport.reportId;
      // 使用言語の計測モードを取得
      this.measurementModeName.objectDistance = this.translate.instant('imageRemeasurementEdit.objectDistance');
      this.measurementModeName.distance = this.translate.instant('imageRemeasurementEdit.distance');
      this.measurementModeName.pointToLine = this.translate.instant('imageRemeasurementEdit.pointToLine');
      this.measurementModeName.depth = this.translate.instant('imageRemeasurementEdit.depth');
      this.measurementModeName.lines = this.translate.instant('imageRemeasurementEdit.lines');
      this.measurementModeName.area = this.translate.instant('imageRemeasurementEdit.area');

      if (changes.inspectionReport.currentValue != changes.inspectionReport.previousValue) {
        this.shouldSkipPreview = false;
        this.previewUrl = null;
      }

      if (this.inspectionReport.thumbFilePath === null || this.inspectionReport.thumbFilePath === '') {
        // Thumbnailがない場合は保存フラグを立てる
        this.isSaveThumbnail = true;
      }

      try {
        this.onInitProcess();
      } catch (e) {
        console.log(e);
        this.oscSpinnerService.hide();
        this.isProcess = false;
      }

      if (this.isFinalProcessing || this.isDraftProcessing) {
        const initialMessage = this.translate.instant('inspectionReport.CreateReportToastInitial', {
          title: this.inspectionReport.title,
          version: this.inspectionReport.version
        });

        this.progressSnackBar.openCustomSnackBar(initialMessage, 'snack-progress-custom');
        this.progressSnackBar.setPageActiveState(true);

        const { totalImagesInReport } = this.trackImageSizes();

        const imageCount = Math.max(10, totalImagesInReport);

        const secPerImage = 3;

        this.progressSnackBar.showProgress(
          this.inspectionReport.title,
          this.inspectionReport.version,
          imageCount,
          secPerImage,
          this.inspectionReport.reportId
        );
      } else {
        this.progressSnackBar.forceCompleteStatus();
      }
    }
    const iframes = [this.iframe, this.ieIframe, this.edgeIframe];

    iframes.forEach((iframeRef) => {
      if (iframeRef && iframeRef.nativeElement) {
        iframeRef.nativeElement.addEventListener('load', () => {
          const imgElements = iframeRef.nativeElement.contentWindow.document.querySelectorAll('img[src*="draft.png"]');

          imgElements.forEach((img) => {
            (img as HTMLImageElement).style.pointerEvents = 'none';
          });
        });
      }
    });
  }

  ngAfterViewInit() {
    window.removeEventListener('message', this.imageClickEvent);
    window.addEventListener('message', this.imageClickEvent);
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.imageClickEvent);
    clearTimeout(this.getReportTimer);
    this.progressSnackBar.setPageActiveState(false);
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  onInitProcess() {
    if (this.previewUrl) return;
    this.oscSpinnerService.show();
    this.cd.detectChanges();

    this.pdfUrl = null;
    this.browserName = this.utilsService.getBrowserName();
    this.deviceName = this.utilsService.getDeviceName();
    this.isPlugin = this.isPdfPlugin();
    this.previewUrl = null;
    this.isProcess = true;
    this.isDone = true;
    this.isError = false;

    this.setReportForDisplay();
    this.validateReEdit();
    if (this.isDesktopAndOffline && !this.offlineReportPreviewService.isInitializing) {
      this.isOfflinePreviewReady = false;
      this.offlineReportPreviewService.isSaveHtml = false;
      this.offlineReportPreviewService.initialize(this.inspectionReport.templateId, this.inspectionReport.reportId, this.inspectionReport.version);
    }
  }

  setReportForDisplay(): void {
    if (this.isCompleted) {
      this.inspDataManageService.createFinalPdfEmit(GEN_FINAL_REPORT_TYPE.GET);
      this.latestVersion = parseInt(this.inspectionReport.version.toString(), 10);
      this.oscSpinnerService.hide();
    } else {
      this.isEditableTextsCheck(this.inspectionReport);
      this.getInspectionReportPreview(this.reportId, this.measurementModeName);
    }
    this.latestVersion = this.inspectionReport.version;
  }

  isEditableTextsCheck(inspectionReport: InspectionReport) {
    if (this.shouldSkipPreview) return;
    if (!inspectionReport || inspectionReport.templateId === '' || inspectionReport.templateId === null) {
      throw new ApplicationError(this.translate.instant('inspectionReport.ErrInvalidInsRpt'));
    }

    if (this.isDesktopAndOffline && !this.offlineReportPreviewService.isInitializing) {
      this.isOfflinePreviewReady = false;
      this.offlineReportPreviewService.isSaveHtml = false;
      this.offlineReportPreviewService.onReady.subscribe(() => {
        this.reportTemplateService.getEditableTexts(inspectionReport.templateId, inspectionReport.reportId)
          .subscribe(item => {
            this.editableTextsList = item;
          },
            error => {
              console.log(error);
              this.snackBar.open(this.translate.instant('inspectionReport.ErrGetRptText'));
            },
            () => {
              this.isEditabletexts = this.editableTextsList.length > 0;
            });
      });
      this.offlineReportPreviewService.initialize(this.inspectionReport.templateId, this.inspectionReport.reportId, this.inspectionReport.version);
    } else {
      this.reportTemplateService.getEditableTexts(inspectionReport.templateId, inspectionReport.reportId)
        .subscribe(item => {
          this.editableTextsList = item;
        },
          error => {
            console.log(error);
            this.snackBar.open(this.translate.instant('inspectionReport.ErrGetRptText'));
          },
          () => {
            if (this.editableTextsList.length <= 0) {
              this.isEditabletexts = false;
            } else {
              this.isEditabletexts = true;
            }
          });
    }
  }

  isPdfPlugin(): boolean {
    let pluginFlg = false;
    const plugin = window.navigator.plugins;
    // safari
    if (this.browserName === BROWSER_TYPE.SAFARI) {
      pluginFlg = true;
    } else {
      for (let i = 0; i < plugin.length; i++) {
        const pluginName = plugin[i].name;
        if (pluginName.match(/PDF/) || pluginName.match(/pdf/) ||
          pluginName.match(/Adobe/) || pluginName.match(/adobe/) ||
          pluginName.match(/Acrobat/) || pluginName.match(/acrobat/)) {
          pluginFlg = true;
        }
      }
    }
    return pluginFlg;
  }

  getSubjectModelIdByInspection(inspection: Inspection): string {
    if (!inspection) {
      throw new ApplicationError(this.translate.instant('inspectionReport.ErrNullIns'));
    }

    return inspection.subjectModelId;
  }

  getInspectionReportPreview(reportId: string, measurementModeName: MeasurementModeName): void {
    if (this.shouldSkipPreview) return;
    if (!reportId) {
      throw new ApplicationError(this.translate.instant('inspectionReport.ErrNullRptID'));
    }
    this.oscSpinnerService.show();
    if (this.previewUrl === null) {
      let tmpBlob: string;
      this.inspectionReportService.getInspectionReportPreview(reportId, measurementModeName).subscribe(item => {
        tmpBlob = item;
        this.previewGenerated.emit(item);
      },
        (error: HttpErrorResponse) => {
          this.oscSpinnerService.hide();
          this.isProcess = false;
          console.log(error);
          if (error.status === 404 || error.status === 500 || error.status === 502) {
            this.openErrorMessageDialog(error, false);
          } else {
            this.snackBar.open(this.translate.instant('inspectionReport.ErrLoad'));
          }
        },
        () => {
          if (tmpBlob === null) {
            this.oscSpinnerService.hide();
            this.isProcess = false;
            throw new ApplicationError(this.translate.instant('inspectionReport.PreviewNotFound'));
          }

          if (this.browserName === BROWSER_TYPE.EDGE) {
            const edgeIframeDoc = this.edgeIframe.nativeElement.contentDocument || this.edgeIframe.nativeElement.contentWindow;
            edgeIframeDoc.open();
            edgeIframeDoc.write(tmpBlob);
            const imgElements = edgeIframeDoc.contentWindow.document.querySelectorAll('img[src*="draft.png"]');
            imgElements.forEach((img) => {
              console.log(img);
              (img as HTMLImageElement).style.pointerEvents = 'none';
            });
            edgeIframeDoc.close();
          } else {
            const blob = new Blob([tmpBlob], { type: 'text/html' });
            this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(blob));
          }
          this.oscSpinnerService.hide();
          this.isProcess = false;
          this.isDone = false;
        });
    } else {
      this.oscSpinnerService.hide();
      this.isProcess = false;
      this.isDone = false;
    }
  }

  /** レポートPDFをダウンロードする
   * @param  {boolean} preview ドラフト版
   */
  downloadInspectionReportPdf(preview?: boolean) {
    if (!this.reportId) {
      throw new ApplicationError(this.translate.instant('inspectionReport.ErrNullRptID'));
    }

    this.isDownload = true;
    this.inspectionReportService.getInspectionReportPdf(this.reportId, this.latestVersion, preview)
      .subscribe({
        next: blob => {
          this.utilsService.download(blob, this.inspectionReport.title + '.pdf', this.reportId);
        },
        error: error => {
          console.log(error);
          this.snackBar.open(this.translate.instant('inspectionReport.ErrDownload'));
          this.isDownload = false;
        },
        complete: () => {
          this.isDownload = false;
        }
      });
  }

  /** ドラフト版PDFが存在したらダウンロード
   * @param  {boolean} preview ドラフト版
   */
  downloadDraftInspectionReportPdf() {
    if (this.isDesktopAndOffline) {
      this.downloadDraftReportPdfInOffline();
    } else {
      this.downloadDraftReportPdfForApi();
    }
  }

  downloadDraftReportPdfForApi() {
    if (!this.reportId) {
      throw new ApplicationError(this.translate.instant('inspectionReport.ErrNullRptID'));
    }

    if (this.browserName === BROWSER_TYPE.SAFARI) {
      window.open('pdf-load', 'child' + this.reportId);
    }

    this.isDownload = true;
    this.inspectionReportService.getInspectionReportPdf(this.reportId, null, true)
      .subscribe(
        blob => {
          this.utilsService.download(blob, this.inspectionReport.title + '.pdf', this.reportId);
        },
        (error: HttpErrorResponse) => {
          this.isProcess = false;
          if (error.status === 404) {
            this.inspDataManageService.createDraftPdfEmit(GEN_DRAFT_REPORT_TYPE.DEFAULT);
          } else {
            console.log(error);
            this.snackBar.open(this.translate.instant('inspectionReport.ErrDownload'));
          }
          this.isDownload = false;
        },
        () => {
          this.isDownload = false;
        }
      );
    this.shouldSkipPreview = false;
  }

  /** ステータスによってボタンの表示非表示を変える
   */
  validateReEdit() {
    if (this.inspectionReport.status === INSPECTION_REPORT_STATUS_CODE.COMPLETED) {
      this.isReEdit = true;
    } else {
      this.isReEdit = false;
    }
  }

  createNewVersionReport(): void {
    if (!this.reportId) {
      throw new ApplicationError(this.translate.instant('inspectionReport.ErrNullRptID'));
    }

    const dialogRef = this.dialog.open(ReEditConfirmDialogComponent, {
      width: '700px'
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        this.oscSpinnerService.show();
        this.isProcess = true;

        this.inspectionReportService
          .createNewVersionInspectionReport(this.reportId)
          .subscribe(
            report => this.inspectionReport = report,
            error => {
              console.log(error);
              this.oscSpinnerService.hide();
              this.isProcess = false;
              this.isReEdit = true;
            },
            () => {
              this.previewUrl = null;
              this.setReportForDisplay();
              this.inspDataManageService.reEditReportEmit(this.inspectionReport);
              this.oscSpinnerService.hide();
              this.isProcess = false;
              this.isReEdit = false;
              this.selectReportService.setViewReport(this.inspectionReport.id, this.inspectionReport.version);
            }
          );
      }
    });
  }

  // Dialog
  openPropertiesDialog(): void {
    if (!this.isProcess) {
      // この検査が持っているsubject modelをload
      const subjectModelId = this.getSubjectModelIdByInspection(this.inspection);
      const dialogRef = this.dialog.open(ReportPropertiesDialogComponent, {
        width: '700px',
        minHeight: '494px',
        autoFocus: false,
        data: {
          selectId: this.inspectionReport.templateId,
          subjectModelId: subjectModelId,
          inspection: this.inspection,
          inspectionReport: this.inspectionReport,
          isDesktopAndOffline: this.isDesktopAndOffline
        }
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result === '' || result === undefined || result === null) {
          dialogRef.close(ReportPropertiesDialogComponent);
        } else {
          this.oscSpinnerService.show();
          this.isProcess = true;
          this.pdfUrl = null;
          this.previewUrl = null;
          setTimeout(() => {
            this.changedReport.emit(this.reportId);
            this.inspDataManageService.updateReportTabEmit(this.inspectionReport);
          }, 1000);
        }
      });
    }
  }

  openEditableTextsDialog(): void {
    let dialogRef;
    let isReload = false;
    if (!this.isProcess) {
      dialogRef = this.dialog.open(ReportEditableTextsEditDialogComponent, {
        width: '800px',
        height: '98%',
        autoFocus: false,
        disableClose: true,
        data: {
          reportId: this.reportId,
          reportTemplateId: this.inspectionReport.templateId,
          inspectionReport: this.inspectionReport,
          isReload: this.isReload,
          browserName: this.browserName,
          editableTextsList: this.editableTextsList
        }
      });
      dialogRef.componentInstance.reload.subscribe(reload => {
        if (reload) {
          this.oscSpinnerService.show();
          this.isProcess = true;
          this.pdfUrl = null;
          this.previewUrl = null;
          isReload = true;
          this.isOfflinePreviewReady = false;
          this.isSaveThumbnail = true;
          setTimeout(() => {
            this.changedReport.emit(this.reportId);
            this.isEditableTextsCheck(this.inspectionReport);
            this.getInspectionReportPreview(this.reportId, this.measurementModeName);
          }, 1000);
        }
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result === undefined || result === null || isReload) {
          dialogRef.close(ReportEditableTextsEditDialogComponent);
        } else {
          this.oscSpinnerService.show();
          this.isProcess = true;
          this.pdfUrl = null;
          this.previewUrl = null;
          this.isOfflinePreviewReady = false;
          this.isSaveThumbnail = true;
          setTimeout(() => {
            this.changedReport.emit(this.reportId);
            this.isEditableTextsCheck(this.inspectionReport);
            this.getInspectionReportPreview(this.reportId, this.measurementModeName);
          }, 1000);
        }
      });
    }
  }

  checkReportValidation(): void {
    this.shouldSkipPreview = true;
    if (!this.isProcess) {
      this.oscSpinnerService.show();
      this.isDone = true;

      if (this.isMissingRequiredEditableTexts()) {
        this.openValidationDialog();
      } else {
        this.openConfirmDialog();
      }
    }
  }

  isMissingRequiredEditableTexts(): boolean {
    let count = 0;
    const tmpInspectionReport = this.inspectionReport;
    this.editableTextsList.forEach(item => {
      // null or empty check
      if (item.validation.required) {
        // measures of backend editableId
        const editableIds = Object.keys(tmpInspectionReport.editableTexts);
        let isExistItemEditableId = false;

        editableIds.forEach(editableId => {
          if (editableId.toLowerCase() === item.editableId.toLowerCase()) {
            if (!tmpInspectionReport.editableTexts[editableId]) {
              count++;
            }
            isExistItemEditableId = true;
          }
        });

        if (!isExistItemEditableId) {
          count++;
        }
      }
    });
    return count > 0;
  }

  openConfirmDialog(): void {
    const data = this.trackImageSizes();
    this.oscSpinnerService.hide();
    const dialogRef = this.dialog.open(ReportCompleteConfirmDialogComponent, {
      width: '550px',
      height: '300px',
      data,
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'yes') {
        this.oscSpinnerService.show();
        this.inspDataManageService.createFinalPdfEmit(GEN_FINAL_REPORT_TYPE.CREATE);
      } else {
        this.oscSpinnerService.hide();
        this.isProcess = false;
      }
      this.isDone = false;
    });
  }

  private trackImageSizes() {
    const finalReportQuality = this.getFinalReportQuality();
    let totalImagesInReport = 0;

    this.inspectionReport.reportLocations.forEach(report => {
      report.imageIds.forEach(imageId => {
        totalImagesInReport += 1;
      });
    });

    return { totalImagesInReport, finalReportQuality };
  }

  openValidationDialog(): void {
    this.oscSpinnerService.hide();
    const dialogRef = this.dialog.open(EditableTextsValidationDialogComponent, {
      width: '410px',
      height: '220px'
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.openEditableTextsDialog();
      }
      this.oscSpinnerService.hide();
      this.isProcess = false;
      this.isDone = false;
    });
  }

  isSafari(browserName): boolean {
    return this.utilsService.isSafari(browserName);
  }

  isEdge(browserName): boolean {
    return this.utilsService.isEdge(browserName);
  }

  isDeviceNameIpad(deviceName): boolean {
    return this.utilsService.isDeviceNameIpad(deviceName);
  }

  isReadOnly(): boolean {

    return !this.inspectionReport.permission || (this.inspectionReport.permission & PERMISSION_CODE.WRITE) === 0;
  }

  openErrorMessageDialog(error: HttpErrorResponse, isDone: boolean): void {
    if (error.status === 404) {
      if (this.apiError.toErrorCode(error) === 'RPH1-404-ReportAuthor') {
        let secondMessage = '';
        if (this.isAdmin) {
          secondMessage = this.translate.instant('inspectionReport.MsgDelRptTab');
        } else {
          secondMessage = this.translate.instant('inspectionReport.MsgDelRptAdmin');
        }
        this.dialog.open(CommonErrorMessageDialogComponent, {
          width: '500px',
          height: '360px',
          disableClose: true,
          data: {
            firstMessage: this.translate.instant('inspectionReport.MsgRptAuthorDel'),
            secondMessage: secondMessage,
          }
        });
      } else if (this.apiError.toErrorCode(error) === 'RPH1-404-Inspector' || this.apiError.toErrorCode(error) === 'RP09-404-Inspector') {
        let secondMessage = '';
        if (this.isReadOnly()) {
          secondMessage = this.translate.instant('inspectionReport.MsgShowRptUpdate');
        } else {
          secondMessage = this.translate.instant('inspectionReport.MsgUncheckAndSave');
        }

        this.dialog.open(CommonErrorMessageDialogComponent, {
          width: '550px',
          height: '390px',
          disableClose: true,
          data: {
            firstMessage: this.translate.instant('inspectionReport.MsgInspectorDel'),
            secondMessage: secondMessage,
          }
        });
      } else {
        this.snackBar.open(this.translate.instant('inspectionReport.MsgRptNotFound'));
      }
    } else if (error.status === 409) {
      this.dialog.open(CommonErrorMessageDialogComponent, {
        width: '500px',
        height: '360px',
        disableClose: true,
        data: {
          firstMessage: this.translate.instant('inspectionReport.MsgRptComplete'),
          secondMessage: this.translate.instant('inspectionReport.MsgRefresh'),
        }
      });
    } else if (error.status === 412) {
      this.dialog.open(CommonErrorMessageDialogComponent, {
        width: '500px',
        height: '360px',
        disableClose: true,
        data: {
          firstMessage: this.translate.instant('inspectionReport.ErrConflict'),
          secondMessage: this.translate.instant('inspectionReport.MsgRefresh'),
        }
      });
    } else if (error.status === 500) {
      this.dialog.open(CommonErrorMessageDialogComponent, {
        width: '500px',
        height: '360px',
        disableClose: true,
        data: {
          firstMessage: this.translate.instant('inspectionReport.SysError'),
          secondMessage: this.translate.instant('inspectionReport.MsgRefresh')
        }
      });
    } else if (!error.status || error.status === 0 || error.status === 502) {
      let firstMessage = '';
      let secondMessage = '';

      if (isDone) {
        this.isProcessingCreateReport = true;
        firstMessage = this.translate.instant('inspectionReport.MsgResponseNotCompleted');
        secondMessage = this.translate.instant('inspectionReport.MsgRefreshProcess');
      } else {
        this.isProcessingCreateReport = false;
        firstMessage = this.translate.instant('inspectionReport.Timeout');
        secondMessage = this.translate.instant('inspectionReport.MsgRefreshOrContact');
      }

      this.dialog.open(CommonErrorMessageDialogComponent, {
        width: '500px',
        height: '380px',
        disableClose: true,
        data: {
          firstMessage: firstMessage,
          secondMessage: secondMessage,
        }
      });
    } else {
      this.snackBar.open(this.translate.instant('inspectionReport.ErrLoad'));
    }
  }

  openShareDialog(): Observable<any> {
    this.oscSpinnerService.show(true);
    const dialogRef = this.dialog.open(CommonShareDialogComponent, {
      width: '800px',
      minWidth: '400px',
      maxHeight: '800px',
      data: { report: this.inspectionReport, isSummary: false }
    });
    return dialogRef.afterClosed();
  }

  shareDraftInspectionReportPdf() {
    if (!this.inspectionReport.reportId || this.inspectionReport.reportId === null || this.inspectionReport.reportId === undefined) {
      throw new ApplicationError(this.translate.instant('inspectionReport.ErrNullRptID'));
    }

    this.inspectionReportService.getInspectionReportSharedLink(this.inspectionReport.reportId, getFormatedExpirationDate(),
      this.inspectionReport.version, this.inspectionReport.status === '000' ? true : false)
      .subscribe(_ => this.openShareDialog(),
        (error: HttpErrorResponse) => {
          this.isProcess = false;
          if (error.status === 404) { // ドラフトが無い or 更新があるため再生成待ち
            this.inspDataManageService.createDraftPdfEmit(GEN_DRAFT_REPORT_TYPE.DEFAULT);
            this.inspDataManageService.shareReport = true;
          } else {
            console.log(error);
            this.snackBar.open(this.translate.instant('inspectionReport.ErrDownload'));
          }
        });
  }

  imageClickEvent = (event: any) => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const component = this;
    if (event.data.action === 'EditImage') {
      component.getNecessaryData(event.data.message);
    }
  };

  // 画像編集ダイアログを立ち上げるのに必要なデータを取得
  getNecessaryData(imageId: string) {
    this.inspectionImage = [];
    zip(
      this.inspectionImageService.getInspectionImage(this.inspectionReport.inspectionIds[0], imageId),
      this.subjectModelService.getSubjectModel(this.inspection.subjectModelId, this.inspection.locationVersion)
    ).subscribe(
      res => {
        const image = this.inspectionImageService.convertInspectionImage2InpsctionImageListItem(res[0]);
        this.inspectionImage.push(image);
        this.subjectModel = res[1];
      },
      error => {
        console.log(error);
        this.oscSpinnerService.hide(true);
      },
      () => {
        this.openInspectionImageEditDialog();
      }
    );
  }

  openInspectionImageEditDialog() {
    this.snackBar.clearAll();

    this.evcModalService.showModal(ImageEditDialogComponent, {
      inspectionImage: this.inspectionImage[0],
      subjectModel: this.subjectModel,
      images: this.inspectionImage,
      index: 0,
      isEditable: this.isEditable,
      inspectionReport: this.inspectionReport,
      isPreviewReport: true,
      applyImage: this.onModalClosed.bind(this),
      applyReport: this.onModalClosed.bind(this),
      changeReportLocation: this.onModalClosed.bind(this),
    });
  }

  onModalClosed() {
    setTimeout(() => {
      this.changedReport.emit(this.reportId);
      this.isEditableTextsCheck(this.inspectionReport);
      this.getInspectionReportPreview(this.reportId, this.measurementModeName);
    }, 1000);
  }

  async downloadDraftReportPdfInOffline() {
    this.oscSpinnerService.show(true);
    this.offlineReportPreviewService.isSaveHtml = true;
    this.offlineReportPreviewService.isPreview = true;
    this.cd.detectChanges();
    const pathSeparator = this.fileSystemAccessService.getSystemPathSeparator();
    const option = { type: 'text/html' };
    const htmlFileName = `draft-${this.inspectionReport.version}.html`;
    // htmlを画面から抽出
    const report = document.getElementById('offline-draft-report');
    if (!report) {
      this.offlineReportPreviewService.isSaveHtml = false;
      this.cd.detectChanges();
      this.snackBar.open('load now');
      return;
    }
    const snapshot = '<html><head><meta charset="utf-8">'
      + '<link rel="stylesheet" type="text/css" href="report.css">'
      + '</head><body style="-webkit-print-color-adjust: exact; background-color: none; margin: 0;">'
      + new XMLSerializer().serializeToString(report)
      + '</body></html>';
    const reportBlob = new Blob([snapshot], option);
    const reportFile = new File([reportBlob], htmlFileName, option);

    // html, png, cssを保存
    const saveFolderPath = `inspections${pathSeparator}${this.inspectionReport.inspectionIds[0]}${pathSeparator}reports${pathSeparator}${this.reportId}${pathSeparator}`;
    const savePath = saveFolderPath + htmlFileName;
    await this.offlineUtilsService.getAppDirectory()
      .toPromise()
      .then(dir => {
        const draftImgPath = `${dir}${pathSeparator}assets${pathSeparator}images${pathSeparator}draft.png`;
        const reportCssPath = `${dir}${pathSeparator}assets${pathSeparator}offline${pathSeparator}report.css`;
        return Promise.all([
          this.blobService.getBlobFromUrl(FILE_PREFIX + draftImgPath),
          this.blobService.getBlobFromUrl(FILE_PREFIX + reportCssPath)
        ]);
      })
      .then(blobs => {
        const draftImgBlob = blobs[0];
        const cssBlob = blobs[1];
        const imgFileName = 'draft.png';
        const cssFileName = 'report.css';
        const draftImg = new File([draftImgBlob], imgFileName, { type: 'image/png' });
        const css = new File([cssBlob], cssFileName, { type: 'text/css' });
        return Promise.all([
          this.fileSystemAccessService.saveFile(draftImg, saveFolderPath + imgFileName),
          this.fileSystemAccessService.saveFile(css, saveFolderPath + cssFileName),
          this.fileSystemAccessService.saveFile(reportFile, saveFolderPath + htmlFileName)
        ]);
      });

    const pdfFileName = `${this.inspectionReport.title}.pdf`;

    const pdfImageQuality = this.getDraftQuality() as ReportImageQuality;

    this.offlineUtilsService.printToPdf(savePath, { pdfImageQuality })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(success => {
        const afterDownloadCleanUp = () => {
          this.offlineReportPreviewService.isSaveHtml = false;
          this.oscSpinnerService.hide(true);
          this.cd.detectChanges();
        };

        if (success) {
          if (this.networkStatusService.useApplicationFileManager()) {
            this.inspectionReportService
              .getInspectionReportPdf(this.reportId, this.inspectionReport.version, true)
              .subscribe({
                next: async pdfReportBlob => {
                  const supportedExtensions = {
                    [DOCUMENT_FILETYPE.EXTENSION_PDF]: DOCUMENT_FILETYPE.PDF,
                  };

                  this.utilsService.showFileManager(pdfFileName, pdfReportBlob, supportedExtensions);
                },
                complete: afterDownloadCleanUp
              });

          } else {
            this.inspectionReportService
              .getInspectionReportPdfUrl(this.reportId, this.inspectionReport.version?.toString(), true)
              .subscribe({
                next: async item => {
                  const a = document.createElement('a');
                  a.download = pdfFileName;
                  a.href = item.url;
                  a.click();

                },
                complete: afterDownloadCleanUp
              });
          }
        } else {
          afterDownloadCleanUp();
          this.snackBar.open(this.translate.instant('inspectionReport.ErrDownload'));
        }
      });
  }

  saveOfflinePreviewHtml() {
    this.offlineReportPreviewService.isSaveHtml = true;
    this.oscSpinnerService.show();

    const pathSeparator = this.fileSystemAccessService.getSystemPathSeparator();
    const option = { type: 'text/html' };
    const withoutMimeFilename = `thumbnail-${this.inspectionReport.version}`;
    const htmlFileName = `${withoutMimeFilename}.html`;
    const thumbFilePath = `${withoutMimeFilename}.png`;
    // htmlを画面から抽出
    const report = document.getElementById('offline-preview-0');

    if (!report) {
      this.offlineReportPreviewService.isSaveHtml = false;
      this.cd.detectChanges();
      this.snackBar.open('load now');
      return;
    }
    const snapshot = '<html><head><meta charset="utf-8">'
      + '<link rel="stylesheet" type="text/css" href="report.css">'
      + '</head><body style="-webkit-print-color-adjust: exact; background-color: none; margin: 0;">'
      + new XMLSerializer().serializeToString(report)
      + '</body></html>';
    const reportBlob = new Blob([snapshot], option);
    const reportFile = new File([reportBlob], htmlFileName, option);

    // html, png, cssを保存
    const saveFolderPath = `inspections${pathSeparator}${this.inspectionReport.inspectionIds[0]}${pathSeparator}reports${pathSeparator}${this.reportId}${pathSeparator}`;
    this.offlineUtilsService.getAppDirectory()
      .pipe(switchMap(dir => {
        const reportCssPath = `${dir}${pathSeparator}assets${pathSeparator}offline${pathSeparator}report.css`;
        return from(this.blobService.getBlobFromUrl(FILE_PREFIX + reportCssPath));
      }))
      .pipe(switchMap((cssBlob: Blob) => {
        const cssFileName = 'report.css';
        const css = new File([cssBlob], cssFileName, { type: 'text/css' });
        return zip(
          from(this.fileSystemAccessService.saveFile(css, saveFolderPath + cssFileName)),
          from(this.fileSystemAccessService.saveFile(reportFile, saveFolderPath + htmlFileName)),
        );
      }))
      .pipe(switchMap(() => this.reportTemplateService.getReportTemplate(this.inspectionReport.templateId)))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(success => {
        if (success) {
          this.individualReportDbService.putThumbsPath(this.reportId, saveFolderPath + thumbFilePath);
        } else {
          this.snackBar.open(this.translate.instant('inspectionReport.ErrDownload'));
        }

        this.offlineReportPreviewService.isSaveHtml = false;
        this.isOfflinePreviewReady = true;
        this.isSaveThumbnail = false;
        this.oscSpinnerService.hide();
        this.cd.detectChanges();
      });
  }

  getPdfOptions(pdfFileName: string, paperSize: string) {
    const draftQuality = this.getDraftQuality();

    const option = new WkHtmlToPdfOptions();

    option.title = pdfFileName;
    option.quality = PDF_COMPRESS_PARAMS_MEDIUM.QUALITY;
    option.imageQuality = PDF_COMPRESS_PARAMS_MEDIUM.QUALITY;

    if (draftQuality === 0) {
      option.dpi = PDF_COMPRESS_PARAMS_HIGH.DPI;
      option.imageDpi = PDF_COMPRESS_PARAMS_HIGH.DPI;
    } else if (draftQuality === 2) {
      option.dpi = PDF_COMPRESS_PARAMS_LOW.DPI;
      option.imageDpi = PDF_COMPRESS_PARAMS_LOW.DPI;
    } else {
      option.dpi = PDF_COMPRESS_PARAMS_MEDIUM.DPI;
      option.imageDpi = PDF_COMPRESS_PARAMS_MEDIUM.DPI;
    }

    switch (paperSize) {
      case 'A4':
        option.pageSize = PaperKind.A4;
        break;
      case 'B5':
        option.pageSize = PaperKind.B5;
        break;
      case 'B5JIS':
        option.pageSize = PaperKind.B5JIS;
        break;
      case 'Legal':
        option.pageSize = PaperKind.Legal;
        break;
      case 'Letter':
      default:
        option.pageSize = PaperKind.Letter;
    }

    return option;
  }

  cancelGeneratePDF(isPreview: boolean) {
    if (isPreview) {
      this.inspDataManageService.createDraftPdfEmit(GEN_DRAFT_REPORT_TYPE.CANCEL);
    } else {
      this.inspDataManageService.createFinalPdfEmit(GEN_FINAL_REPORT_TYPE.CANCEL);
    }
  }

  previewRendered(isRendered: boolean) {
    if (this.inspectionReport.thumbFilePath === null || this.inspectionReport.thumbFilePath === '') {
      // Thumbnailがない場合は保存フラグを立てる
      this.isSaveThumbnail = true;
    } else {
      this.isOfflinePreviewReady = true;
    }
    if (this.isSaveThumbnail && isRendered) {
      this.oscSpinnerService.show();
      setTimeout(() => this.saveOfflinePreviewHtml(), 200);
    }
  }

  private getDraftQuality() {
    const configs = this.tenantConfigService.tenantConfig;

    const draftReportQuality =  this.inspectionReport.draftQuality !== null ? this.inspectionReport.draftQuality :
      configs.find(entry => entry.key === TenantConfig.DefaultImageQualityOfDraftReports.KEY).value;

    return draftReportQuality;
  }

  private getFinalReportQuality() {
    const configs = this.tenantConfigService.tenantConfig;

    const finalReportQuality = this.inspectionReport.finalQuality !== null
      ? this.inspectionReport.finalQuality
      : configs.find(entry => entry.key === TenantConfig.DefaultImageQualityOfFinalReports.KEY).value;

    return finalReportQuality;
  }
}
